import { HttpRequest } from '../../../utils/httpClient';
import { Flow, FlowConversation, FlowAction, FlowStatus, ProcessFlow } from '../types';

import { createLogger } from '../../../utils/logger';
const logger = createLogger('features-assistants');

const API_BASE_URL = process.env.REACT_APP_API_HOST || 'https://api.dev.mind.creai.cloud';

export const flowService = {
  fetchProcessFlowCards: async (assistantId: string): Promise<ProcessFlow[]> => {
    const url = `${API_BASE_URL}/v1/workflows`;
    logger.info('Making request to:', url);
    logger.info('With params:', { assistantId });

    const flowRequest = new HttpRequest(url);
    const response = await flowRequest.get('', { assistantId:assistantId } );

    logger.info('Full response:', response);
    // The backend returns { success: true, data: workflows[], meta: {...} }
    // So we need to access response.data.data, not response.data.data.data
    return response.data.data || [];
  },

  activateWorkflow: async (workflowId: string): Promise<ProcessFlow> => {
    const url = `${API_BASE_URL}/v1/workflows/${workflowId}/activate`;
    const request = new HttpRequest(url);
    const response = await request.post();
    return response.data.data;
  },

  deactivateWorkflow: async (workflowId: string): Promise<ProcessFlow> => {
    const url = `${API_BASE_URL}/v1/workflows/${workflowId}/deactivate`;
    const request = new HttpRequest(url);
    const response = await request.post();
    return response.data.data;
  },

  deleteProcessFlow: async (workflowId: string): Promise<void> => {
    const url = `${API_BASE_URL}/v1/workflows`;
    const request = new HttpRequest(url);
    await request.delete(workflowId);
  },

  fetchFlows: async (chatbotId: string): Promise<Flow[]> => {
    const request = new HttpRequest(`${API_BASE_URL}/flows`);
    const response = await request.get(`chatbot/${chatbotId}`);
    return response?.data?.flows || [];
  },

  fetchFlow: async (flowId: string): Promise<Flow> => {
    const flowRequest = new HttpRequest(`${API_BASE_URL}/flows`);
    const response = await flowRequest.get(flowId);
    return response.data.flowData;
  },

  createFlow: async (flowData: { name: string; description: string; chatbotId: string; status: number }): Promise<Flow> => {
    const flowRequest = new HttpRequest(`${API_BASE_URL}/flows`);
    const response = await flowRequest.post(JSON.stringify(flowData));
    return response.data.flowData;
  },

  updateFlow: async (flowId: string, flowData: { name: string; description: string; chatbotId: string; status: number }): Promise<void> => {
    const flowRequest = new HttpRequest(`${API_BASE_URL}/flows`);
    await flowRequest.put(`${flowId}`, flowData);
  },

  deleteFlow: async (flowId: string): Promise<void> => {
    const flowRequest = new HttpRequest(`${API_BASE_URL}/flows`);
    await flowRequest.delete(flowId);
  },

  fetchAction: async (actionId: string): Promise<any> => {
    const actionRequest = new HttpRequest(`${API_BASE_URL}/actions`);
    const response = await actionRequest.get(actionId);
    return response.data.actionData;
  },

  fetchConversation: async (flowId: string): Promise<FlowConversation> => {
    const request = new HttpRequest(`${API_BASE_URL}/flows`);
    const response = await request.get(`conversation/${flowId}`);
    return response?.data?.conversation || { messages: [] };
  },

  duplicateFlow: async (flowId: string, chatbotId: string, flowData: Flow): Promise<Flow> => {
    // 1. Get original messages
    const conversationRequest = new HttpRequest(`${API_BASE_URL}/flows/conversation`);
    const originalMessagesResponse = await conversationRequest.get(`${flowId}`);
    const originalMessages = originalMessagesResponse.data;

    // 2. Create new flow
    const newFlowData = {
      chatbotId: chatbotId,
      name: `${flowData.name} (Copy)`,
      description: flowData.description,
      status: 0 as FlowStatus,
    };

    const flowRequest = new HttpRequest(`${API_BASE_URL}/flows`);
    const newFlowResponse = await flowRequest.post(newFlowData);
    const newFlowId = newFlowResponse.data.id;

    // 3. Assign messages to new flow
    await conversationRequest.post({
      flowId: newFlowId,
      messages: originalMessages,
    });

    return newFlowResponse.data;
  },

  updateFlowStatus: async (flowId: string, status: FlowStatus): Promise<void> => {
    const flowRequest = new HttpRequest(`${API_BASE_URL}/flows`);
    await flowRequest.put(`${flowId}`, { status });
  },

  updateFlowPrompt: async (chatbotId: string): Promise<void> => {
    const flowPromptRequest = new HttpRequest(`${API_BASE_URL}/flows/flow-prompt/${chatbotId}`);
    await flowPromptRequest.put('', {});
  }
};
